/**
 * Hook de gestion du consentement cookies
 * Conforme RGPD et CDC Juridique v1.2 §9.6
 */

import { useState, useEffect, useCallback } from 'react'
import {
  COOKIE_CONSENT_KEY,
  CookieConsent,
  defaultConsent,
} from '../config/cookieConfig'

export interface UseCookieConsentReturn {
  consent: CookieConsent | null
  hasConsented: boolean
  acceptAll: () => void
  refuseAll: () => void
  updateConsent: (consent: Partial<CookieConsent>) => void
}

/**
 * Verifie si localStorage est disponible
 */
function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__test__'
    localStorage.setItem(testKey, testKey)
    localStorage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

/**
 * Lit le consentement depuis localStorage
 */
function readConsent(): CookieConsent | null {
  if (!isLocalStorageAvailable()) {
    return null
  }

  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as CookieConsent
      // Validation basique
      if (typeof parsed.essential === 'boolean' && typeof parsed.timestamp === 'number') {
        return parsed
      }
    }
  } catch {
    // JSON invalide ou autre erreur
  }

  return null
}

/**
 * Sauvegarde le consentement dans localStorage
 */
function saveConsent(consent: CookieConsent): void {
  if (!isLocalStorageAvailable()) {
    return
  }

  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent))
  } catch {
    // Quota depasse ou autre erreur
  }
}

/**
 * Hook pour gerer le consentement cookies
 */
export function useCookieConsent(): UseCookieConsentReturn {
  const [consent, setConsent] = useState<CookieConsent | null>(null)
  const [initialized, setInitialized] = useState(false)

  // Lecture initiale au mount
  useEffect(() => {
    const storedConsent = readConsent()
    setConsent(storedConsent)
    setInitialized(true)
  }, [])

  // hasConsented = consentement deja donne (accepte ou refuse)
  const hasConsented = initialized && consent !== null

  // Accepter tous les cookies
  const acceptAll = useCallback(() => {
    const newConsent: CookieConsent = {
      essential: true,
      analytics: true,
      functional: true,
      timestamp: Date.now(),
    }
    setConsent(newConsent)
    saveConsent(newConsent)
  }, [])

  // Refuser tous les cookies optionnels
  const refuseAll = useCallback(() => {
    const newConsent: CookieConsent = {
      ...defaultConsent,
      timestamp: Date.now(),
    }
    setConsent(newConsent)
    saveConsent(newConsent)
  }, [])

  // Mise a jour partielle du consentement
  const updateConsent = useCallback((updates: Partial<CookieConsent>) => {
    setConsent((prev) => {
      const newConsent: CookieConsent = {
        essential: true, // Toujours true
        analytics: updates.analytics ?? prev?.analytics ?? false,
        functional: updates.functional ?? prev?.functional ?? false,
        timestamp: Date.now(),
      }
      saveConsent(newConsent)
      return newConsent
    })
  }, [])

  return {
    consent,
    hasConsented,
    acceptAll,
    refuseAll,
    updateConsent,
  }
}

export default useCookieConsent
